import React, { forwardRef } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils.js";

export interface AccordionItemDef {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface AccordionProps {
  items: AccordionItemDef[];
  /** Allow several panels open at once (default: single). */
  multiple?: boolean;
  /** Uncontrolled initial open ids. */
  defaultOpenIds?: string[];
  className?: string;
}

/* Disclosure group (guide §10 "Accordion", register #12). Native disclosure
   contract: each header is a real <button> (Enter/Space toggle for free) with
   aria-expanded + aria-controls; panels are labelled regions.

   ADR-0091: the panel is NOT unmounted on close. `{open && <div>}` can animate
   neither direction — there is nothing on screen to collapse — so every panel
   stays mounted inside a `pb-disclosure` 0fr→1fr grid row that animates to the
   panel's OWN height without anyone measuring it. Staying mounted is what makes
   hiding a collapsed panel from the a11y tree and the tab order a correctness
   requirement rather than a nicety. */
export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  function Accordion(
    { items, multiple = false, defaultOpenIds = [], className },
    ref,
  ) {
    const reactId = React.useId();
    const [openIds, setOpenIds] = React.useState<Set<string>>(
      () => new Set(defaultOpenIds),
    );
    const panelRefs = React.useRef(new Map<string, HTMLDivElement>());

    // React 18 serialises `inert={false}` as the string "false", which HTML
    // reads as inert, so the attribute can only be toggled imperatively.
    React.useLayoutEffect(() => {
      for (const [id, node] of panelRefs.current) {
        node.toggleAttribute("inert", !openIds.has(id));
      }
    }, [openIds, items]);

    const toggle = (id: string) => {
      setOpenIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else {
          if (!multiple) next.clear();
          next.add(id);
        }
        return next;
      });
    };

    return (
      <div
        ref={ref}
        className={cn(
          "w-full divide-y divide-pb-border rounded-lg border border-pb-border bg-pb-card",
          className,
        )}
      >
        {items.map((item) => {
          const open = openIds.has(item.id);
          const expanded = open ? "true" : "false";
          const headerId = `${reactId}-${item.id}-header`;
          const panelId = `${reactId}-${item.id}-panel`;
          return (
            <div key={item.id}>
              <h3 className="m-0">
                <button
                  type="button"
                  id={headerId}
                  aria-expanded={open}
                  aria-controls={panelId}
                  disabled={item.disabled}
                  onClick={() => toggle(item.id)}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 px-4 py-3 text-start text-sm font-medium text-pb-text transition-colors",
                    "enabled:hover:pb-overlay-paint active:bg-pb-border/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pb-teal",
                    item.disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <span className="min-w-0 flex-1">{item.title}</span>
                  {/* One caret that turns. Swapping between two chevron glyphs
                      is a different element each way, so it can never animate. */}
                  <ChevronRight
                    aria-hidden="true"
                    data-expanded={expanded}
                    className="pb-disclosure-caret h-4 w-4 shrink-0 text-pb-text-muted"
                  />
                </button>
              </h3>
              <div data-expanded={expanded} className="pb-disclosure">
                <div
                  ref={(node) => {
                    if (node) panelRefs.current.set(item.id, node);
                    else panelRefs.current.delete(item.id);
                  }}
                  id={panelId}
                  role="region"
                  aria-labelledby={headerId}
                  className="pb-disclosure-panel"
                  {...(open ? {} : { "aria-hidden": "true" as const })}
                >
                  <div className="px-4 pb-4 text-sm leading-relaxed text-pb-text-secondary">
                    {item.content}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  },
);
